import { memo, useCallback } from 'react';
import {
  printCountdownDate,
  calculateTimeDifference,
} from '@betty/toolkit/utils';
import { useTranslation } from '@betty/i18n';
import { Box, Icon, Image, Text, PressableOverlay } from '@betty/moonlight';
import { PaymentMethodVerificationCardProps } from './PaymentMethodVerificationCard.types';

function PaymentMethodVerificationCard({
  cardVendor,
  paymentEntityId,
  uniqueIdentifier,
  expiryDate,
  lastUse,
  selected,
  onSelect,
}: PaymentMethodVerificationCardProps) {
  const { t } = useTranslation('wallet');

  const handleSelect = useCallback(() => {
    onSelect(paymentEntityId);
  }, [paymentEntityId, onSelect]);

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      padding="space-400"
      borderRadius="radius-200"
      backgroundColor="mutedBackground"
      variant={selected ? 'outline-gradient' : 'default'}
    >
      <Icon name={cardVendor.toLowerCase() as Lowercase<typeof cardVendor>} />
      <Box flex={1} paddingHorizontal="space-400">
        <Text variant="body-m" color="darkSurfacePrimary">
          {uniqueIdentifier}
        </Text>
        <Box flexDirection="row" gap="space-100">
          <Text variant="caption" color="darkSurfaceMuted">
            {t('paymentMethodVerificationCardLastUse', {
              date: printCountdownDate(calculateTimeDifference(lastUse), true),
            })}
          </Text>
          <Text variant="caption" color="darkSurfaceMuted">
            {t('paymentMethodVerificationCardExpiration', {
              date: expiryDate,
              interpolation: { escapeValue: false },
            })}
          </Text>
        </Box>
      </Box>
      <Image
        name={selected ? 'wallet.checked' : 'wallet.unchecked'}
        width="space-600"
        height="space-600"
      />
      <PressableOverlay onPress={handleSelect} />
    </Box>
  );
}

export default memo(PaymentMethodVerificationCard);
